/**
 * LevelUpOverlay — Global level-up celebration
 * Watches authStore for level changes and shows a brief celebration overlay.
 */

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { celebrate } from '@/lib/celebrate';
import { playSound } from '@/utils/odisseia-audio';

export function LevelUpOverlay() {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const prevLevelRef = useRef<number | null>(null);
  const [showLevel, setShowLevel] = useState<number | null>(null);

  useEffect(() => {
    const level = user?.level;
    if (level == null) return;

    if (prevLevelRef.current !== null && level > prevLevelRef.current) {
      // Level up detected!
      setShowLevel(level);
      celebrate.rankUp();
      try { playSound('xp-gain'); } catch {}

      // Auto-dismiss after 3 seconds
      const timer = setTimeout(() => setShowLevel(null), 3000);
      return () => clearTimeout(timer);
    }

    prevLevelRef.current = level;
  }, [user?.level]);

  if (showLevel === null) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'none',
        animation: 'levelup-fade 3s ease both',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 8,
          animation: 'levelup-pop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both',
        }}
      >
        <Sparkles size={28} style={{ color: '#D4AF37', filter: 'drop-shadow(0 0 8px rgba(212,175,55,0.6))' }} />
        <span
          style={{
            fontSize: '0.72rem',
            fontWeight: 700,
            color: 'rgba(255,255,255,0.7)',
            textTransform: 'uppercase',
            letterSpacing: 2,
          }}
        >
          {t('levelUp.label', 'LEVEL UP')}
        </span>
        <span
          style={{
            fontSize: '2.5rem',
            fontWeight: 800,
            color: '#D4AF37',
            fontFamily: "'JetBrains Mono', monospace",
            textShadow: '0 0 20px rgba(212,175,55,0.5), 0 2px 10px rgba(0,0,0,0.5)',
          }}
        >
          Lv.{showLevel}
        </span>
      </div>

      <style>{`
        @keyframes levelup-fade {
          0% { background: rgba(0,0,0,0); }
          10% { background: rgba(0,0,0,0.5); }
          70% { background: rgba(0,0,0,0.5); }
          100% { background: rgba(0,0,0,0); opacity: 0; }
        }
        @keyframes levelup-pop {
          0% { transform: scale(0.5); opacity: 0; }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
